// Three-step re-issue flow (S13, replaces the deleted AnularCobroModal):
// 1) live preview via useReemitirCobroPreview, 2) reason + confirm bound to the
// preview fingerprint, 3) terminal result (close + refresh). A 409
// STALE_PREVIEW re-enters step 1 from the fresh payload in the 409 body; any
// other 4xx/5xx falls back to a generic error with a preview recompute — the
// stale preview is never re-displayed. Mirrors AnularCobroModal's antd Modal
// structure + required-motivo validation (now enforced by a blocked confirm).
import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Button, Input, Modal, Spin, message, theme, Typography } from 'antd';
import axios from 'axios';
import {
  REISSUE_ERROR,
  ReemitirPreviewSchema,
  formatCurrency,
  type ReemitirPreview,
} from '@aid/shared';
import { useReemitirCobro, useReemitirCobroPreview } from '../api/useCobros';
import { useT } from '../../../locales';

const { TextArea } = Input;
const { Text } = Typography;

interface ReemitirCobroModalProps {
  open: boolean;
  cobroId: number;
  idCliente: number;
  onClose: () => void;
  onSuccess?: () => void;
}

type PreviewFailure =
  | { kind: 'NOT_FOUND' }
  | { kind: 'OUT_OF_WINDOW' }
  | { kind: 'PACKAGE_LINKED'; pointer: string | null }
  | { kind: 'NO_BILLABLE_LINES' }
  | { kind: 'GENERIC' };

interface ReissueErrorShape {
  status: number;
  code: string | null;
  details: Record<string, unknown> | null;
}

/** Reads the 4xx/5xx body shape propagated by AllExceptionsFilter. */
function readReissueError(error: unknown): ReissueErrorShape {
  if (!axios.isAxiosError(error)) {
    return { status: 0, code: null, details: null };
  }
  const data: unknown = error.response?.data;
  const body = data !== null && typeof data === 'object' ? (data as Record<string, unknown>) : null;
  const code = body !== null && typeof body.error === 'string' ? body.error : null;
  const details =
    body !== null && body.details !== null && typeof body.details === 'object'
      ? (body.details as Record<string, unknown>)
      : null;
  return { status: error.response?.status ?? 0, code, details };
}

function classifyPreviewError(error: unknown): PreviewFailure {
  const { status, code, details } = readReissueError(error);
  switch (code) {
    case REISSUE_ERROR.OUT_OF_WINDOW:
      return { kind: 'OUT_OF_WINDOW' };
    case REISSUE_ERROR.PACKAGE_LINKED:
      return {
        kind: 'PACKAGE_LINKED',
        pointer: typeof details?.reMaterialisation === 'string' ? details.reMaterialisation : null,
      };
    case REISSUE_ERROR.NO_BILLABLE_LINES:
      return { kind: 'NO_BILLABLE_LINES' };
    default:
      break;
  }
  // Unknown 409 code / 404 / anything else: no re-display path, retry is safe.
  if (status === 404) return { kind: 'NOT_FOUND' };
  return { kind: 'GENERIC' };
}

function MoneyCell({
  label,
  value,
  emphasized = false,
}: {
  label: string;
  value: string;
  emphasized?: boolean;
}) {
  const { token } = theme.useToken();
  const isZero = Number(value) === 0;
  const color = isZero
    ? token.colorTextSecondary
    : emphasized
      ? token.colorPrimary
      : token.colorText;
  return (
    <div style={{ minWidth: 0 }}>
      <Text
        type="secondary"
        style={{
          color: token.colorTextTertiary,
          display: 'block',
          fontSize: 11,
          lineHeight: 1.25,
          marginBottom: 4,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Text>
      <Text
        strong={emphasized && !isZero}
        style={{
          color,
          display: 'block',
          fontSize: emphasized ? 14 : 13,
          fontWeight: emphasized && !isZero ? 600 : undefined,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatCurrency(value)}
      </Text>
    </div>
  );
}

/** Inline terminal/transient state box (error alert or warning banner). */
function Notice({
  tone,
  title,
  children,
}: {
  tone: 'error' | 'warning';
  title: string;
  children?: ReactNode;
}) {
  const { token } = theme.useToken();
  const background = tone === 'error' ? token.colorErrorBg : token.colorWarningBg;
  const border = tone === 'error' ? token.colorErrorBorder : token.colorWarningBorder;
  const foreground = tone === 'error' ? token.colorError : token.colorWarning;
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      aria-live={tone === 'error' ? 'assertive' : 'polite'}
      style={{
        background,
        border: `1px solid ${border}`,
        borderRadius: token.borderRadiusSM,
        display: 'grid',
        gap: 6,
        padding: '10px 12px',
      }}
    >
      <Text strong style={{ color: foreground, fontSize: 13 }}>
        {title}
      </Text>
      {children}
    </div>
  );
}

export function ReemitirCobroModal({
  open,
  cobroId,
  idCliente,
  onClose,
  onSuccess,
}: ReemitirCobroModalProps) {
  const t = useT();
  const { token } = theme.useToken();
  const previewMutation = useReemitirCobroPreview();
  const reemitir = useReemitirCobro();
  const [preview, setPreview] = useState<ReemitirPreview | null>(null);
  const [previewFailure, setPreviewFailure] = useState<PreviewFailure | null>(null);
  const [staleNotice, setStaleNotice] = useState(false);
  const [commandFailure, setCommandFailure] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const retryPreview = () => setReloadKey((key) => key + 1);

  // Step 1 on open (+ manual recompute): read-only preview, never the command.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPreview(null);
    setPreviewFailure(null);
    setStaleNotice(false);
    setCommandFailure(false);
    void previewMutation.mutateAsync(cobroId).then(
      (data) => {
        if (!cancelled) setPreview(data);
      },
      (error: unknown) => {
        if (!cancelled) setPreviewFailure(classifyPreviewError(error));
      },
    );
    return () => {
      cancelled = true;
    };
    // previewMutation.mutateAsync intentionally omitted: it changes per render
    // and including it would refetch on every state update.
  }, [open, reloadKey, cobroId]);

  const handleConfirm = async () => {
    const reason = motivo.trim();
    // Mirrors the deleted AnularCobroModal required-motivo validation; the
    // confirm button is disabled while empty, this keeps the invariant.
    if (!reason || preview === null) return;
    setCommandFailure(false);
    try {
      // operacionKey is omitted: the server mints it — UI retries are new
      // operations, never the same one replayed.
      await reemitir.mutateAsync({
        id: cobroId,
        idCliente,
        dto: { motivo: reason, previewFingerprint: preview.fingerprint },
      });
      message.success(t('pages.cobros.reemitirSuccess'));
      onSuccess?.();
      onClose();
    } catch (error) {
      const { code, details } = readReissueError(error);
      if (code === REISSUE_ERROR.STALE_PREVIEW && details !== null) {
        const parsed = ReemitirPreviewSchema.safeParse(details.preview);
        if (parsed.success) {
          // Re-enter step 1 from the fresh payload; the reason is kept and the
          // confirm re-arms with the NEW fingerprint.
          setPreview(parsed.data);
          setStaleNotice(true);
          return;
        }
      }
      // Other 4xx/5xx: generic error, never re-display the stale preview.
      setCommandFailure(true);
    }
  };

  const retryPreviewButton = (
    <Button type="link" size="small" onClick={retryPreview} style={{ padding: 0 }}>
      {t('pages.cobros.reemitirRetry')}
    </Button>
  );

  let body: ReactElement;
  if (previewMutation.isPending) {
    body = (
      <div style={{ alignItems: 'center', display: 'flex', gap: 8, padding: '12px 0' }}>
        <Spin size="small" />
        <Text type="secondary">{t('pages.cobros.reemitirPreviewLoading')}</Text>
      </div>
    );
  } else if (previewFailure !== null) {
    // Terminal conflicts have no retry affordance (close is the only exit);
    // NOT_FOUND / GENERIC can be recomputed.
    const terminal =
      previewFailure.kind === 'OUT_OF_WINDOW' ||
      previewFailure.kind === 'PACKAGE_LINKED' ||
      previewFailure.kind === 'NO_BILLABLE_LINES';
    const label =
      previewFailure.kind === 'OUT_OF_WINDOW'
        ? t('pages.cobros.reemitirOutOfWindow')
        : previewFailure.kind === 'PACKAGE_LINKED'
          ? t('pages.cobros.reemitirPackageLinked')
          : previewFailure.kind === 'NO_BILLABLE_LINES'
            ? t('pages.cobros.reemitirNoBillableLines')
            : previewFailure.kind === 'NOT_FOUND'
              ? t('pages.cobros.notFound')
              : t('pages.cobros.reemitirError');
    body = (
      <Notice tone="error" title={label}>
        {previewFailure.kind === 'PACKAGE_LINKED' && previewFailure.pointer !== null ? (
          <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
            {previewFailure.pointer}
          </Text>
        ) : null}
        {terminal ? null : retryPreviewButton}
      </Notice>
    );
  } else if (commandFailure) {
    body = (
      <Notice tone="error" title={t('pages.cobros.reemitirError')}>
        {retryPreviewButton}
      </Notice>
    );
  } else {
    const delta = preview !== null ? Number(preview.nuevoValor) - Number(preview.valorOriginal) : 0;
    const deltaText = `${delta > 0 ? '+' : delta < 0 ? '−' : ''}${formatCurrency(
      Math.abs(delta).toFixed(2),
    )}`;
    body = (
      <div style={{ display: 'grid', gap: 12, marginTop: 8 }}>
        {staleNotice ? (
          <Notice tone="warning" title={t('pages.cobros.reemitirStalePreview')} />
        ) : null}
        {preview !== null ? (
          <>
            <div
              style={{
                alignItems: 'center',
                display: 'grid',
                gap: 12,
                gridTemplateColumns: '1fr auto 1fr',
              }}
            >
              <MoneyCell label={t('pages.cobros.reemitirValorOriginal')} value={preview.valorOriginal} />
              <ArrowRightOutlined aria-hidden="true" style={{ color: token.colorTextSecondary }} />
              <MoneyCell
                label={t('pages.cobros.reemitirNuevoValor')}
                value={preview.nuevoValor}
                emphasized
              />
            </div>
            <Text type="secondary" style={{ fontSize: 12, textAlign: 'center' }}>
              {t('pages.cobros.reemitirDelta')}: {deltaText}
            </Text>
            <div
              style={{
                display: 'grid',
                gap: 10,
                gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))',
              }}
            >
              <MoneyCell label={t('pages.cobros.reemitirReservadoPagos')} value={preview.reservadoPagos} />
              <MoneyCell
                label={t('pages.cobros.reemitirReservadoCreditos')}
                value={preview.reservadoCreditos}
              />
              <MoneyCell
                label={t('pages.cobros.reemitirCreditoConsumible')}
                value={preview.creditoConsumible}
              />
              <MoneyCell label={t('pages.cobros.reemitirSaldoPrevisto')} value={preview.saldoPrevisto} />
              <MoneyCell
                label={t('pages.cobros.reemitirExcedentePrevisto')}
                value={preview.excedentePrevisto}
              />
            </div>
          </>
        ) : null}
        <TextArea
          placeholder={t('pages.cobros.reemitirMotivoPlaceholder')}
          value={motivo}
          onChange={(event) => setMotivo(event.target.value)}
          rows={3}
        />
      </div>
    );
  }

  return (
    <Modal
      title={t('pages.cobros.reemitir')}
      open={open}
      onOk={handleConfirm}
      onCancel={onClose}
      confirmLoading={reemitir.isPending}
      okText={t('pages.cobros.reemitirConfirmButton')}
      okButtonProps={{ disabled: motivo.trim() === '' || preview === null }}
      centered
      width={520}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
    >
      <p>{t('pages.cobros.reemitirConfirm')}</p>
      {body}
    </Modal>
  );
}
